/**
 * Distribuciones por transformada inversa: un número uniforme por valor (doc. 04 §10.2).
 * Así, un cambio en un parámetro desplaza el valor en la misma dirección en todos los escenarios.
 * Las unidades del resultado son las de los parámetros (en el dominio, minutos).
 */

export type Distribucion =
  | { tipo: 'constante'; valor: number }
  | { tipo: 'triangular'; min: number; tipico: number; max: number }
  | { tipo: 'uniforme'; min: number; max: number }
  | { tipo: 'exponencial'; media: number; truncarEn?: number }
  | { tipo: 'lognormal'; media: number; cv: number; truncarEn?: number }
  | { tipo: 'lognormal-mediana'; mediana: number; p95: number; truncarEn?: number };

/**
 * Inversa de la normal estándar (aproximación racional de Acklam, error relativo < 1,15e-9).
 */
export function normalInversa(p: number): number {
  if (!(p > 0 && p < 1)) throw new Error(`Probabilidad fuera de (0, 1): ${p}`);
  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2,
    -3.066479806614716e1, 2.506628277459239,
  ] as const;
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1,
    -1.328068155288572e1,
  ] as const;
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734,
    4.374664141464968, 2.938163982698783,
  ] as const;
  const d = [
    7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416,
  ] as const;
  const bajo = 0.02425;
  if (p < bajo) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
  if (p > 1 - bajo) {
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return -(
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
  const q = p - 0.5;
  const r = q * q;
  return (
    ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  );
}

/**
 * Función de distribución de la normal estándar (Abramowitz y Stegun 7.1.26 vía erf, error
 * absoluto < 1,5e-7). Se usa solo para truncar la lognormal, donde esa precisión sobra.
 */
export function normalAcumulada(x: number): number {
  const t = 1 / (1 + 0.3275911 * (Math.abs(x) / Math.SQRT2));
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-(x * x) / 2);
  return x >= 0 ? 0.5 * (1 + y) : 0.5 * (1 - y);
}

function parametrosLognormal(
  d: Extract<Distribucion, { tipo: 'lognormal' | 'lognormal-mediana' }>,
): {
  mu: number;
  sigma: number;
} {
  if (d.tipo === 'lognormal') {
    const s2 = Math.log(1 + d.cv * d.cv);
    return { mu: Math.log(d.media) - s2 / 2, sigma: Math.sqrt(s2) };
  }
  return { mu: Math.log(d.mediana), sigma: Math.log(d.p95 / d.mediana) / 1.6448536269514722 };
}

/** Muestra un valor de la distribución con el uniforme `u` ∈ (0, 1). */
export function muestrear(d: Distribucion, u: number): number {
  switch (d.tipo) {
    case 'constante':
      return d.valor;
    case 'uniforme':
      return d.min + u * (d.max - d.min);
    case 'triangular': {
      const { min: a, tipico: c, max: b } = d;
      if (b === a) return a;
      const fc = (c - a) / (b - a);
      return u < fc
        ? a + Math.sqrt(u * (b - a) * (c - a))
        : b - Math.sqrt((1 - u) * (b - a) * (b - c));
    }
    case 'exponencial': {
      // Truncamiento explícito en [0, truncarEn]: F(0) + u·(F(b) − F(0)), sin descartar valores.
      const fb = d.truncarEn === undefined ? 1 : 1 - Math.exp(-d.truncarEn / d.media);
      return -d.media * Math.log(1 - u * fb);
    }
    case 'lognormal':
    case 'lognormal-mediana': {
      const { mu, sigma } = parametrosLognormal(d);
      let p = u;
      if (d.truncarEn !== undefined) {
        const fb = normalAcumulada((Math.log(d.truncarEn) - mu) / sigma);
        p = Math.min(Math.max(u * fb, 1e-15), 1 - 1e-15);
      }
      return Math.exp(mu + sigma * normalInversa(p));
    }
  }
}

/** Media teórica (sin truncamiento), útil para capacidad analítica y textos de ayuda. */
export function mediaTeorica(d: Distribucion): number {
  switch (d.tipo) {
    case 'constante':
      return d.valor;
    case 'uniforme':
      return (d.min + d.max) / 2;
    case 'triangular':
      return (d.min + d.tipico + d.max) / 3;
    case 'exponencial':
      return d.media;
    case 'lognormal':
      return d.media;
    case 'lognormal-mediana': {
      const { mu, sigma } = parametrosLognormal(d);
      return Math.exp(mu + (sigma * sigma) / 2);
    }
  }
}

/**
 * Problemas de coherencia de una distribución (doc. 03 §4, regla 4). Lista vacía si es válida.
 * `conNegativos`: admite valores negativos (por ejemplo, minutos antes de la hora prometida).
 */
export function problemasDistribucion(d: Distribucion, conNegativos = false): string[] {
  const p: string[] = [];
  const finito = (x: number) => Number.isFinite(x);
  const piso = conNegativos ? -Infinity : 0;
  switch (d.tipo) {
    case 'constante':
      if (!finito(d.valor) || d.valor < piso)
        p.push(
          conNegativos
            ? 'El valor debe ser un número.'
            : 'El valor debe ser un número mayor o igual a cero.',
        );
      break;
    case 'uniforme':
      if (!finito(d.min) || !finito(d.max) || d.min < piso || d.min > d.max)
        p.push(
          conNegativos ? 'Debe cumplirse mínimo ≤ máximo.' : 'Debe cumplirse 0 ≤ mínimo ≤ máximo.',
        );
      break;
    case 'triangular':
      if (
        ![d.min, d.tipico, d.max].every(finito) ||
        d.min < piso ||
        d.min > d.tipico ||
        d.tipico > d.max
      )
        p.push(
          conNegativos
            ? 'Debe cumplirse mínimo ≤ típico ≤ máximo.'
            : 'Debe cumplirse 0 ≤ mínimo ≤ típico ≤ máximo.',
        );
      break;
    case 'exponencial':
      if (!finito(d.media) || d.media <= 0) p.push('La media debe ser mayor que cero.');
      if (d.truncarEn !== undefined && !(d.truncarEn > 0))
        p.push('El truncamiento debe ser mayor que cero.');
      break;
    case 'lognormal':
      if (!finito(d.media) || d.media <= 0 || !finito(d.cv) || d.cv <= 0)
        p.push('La media y el coeficiente de variación deben ser mayores que cero.');
      if (d.truncarEn !== undefined && !(d.truncarEn > 0))
        p.push('El truncamiento debe ser mayor que cero.');
      break;
    case 'lognormal-mediana':
      if (!finito(d.mediana) || d.mediana <= 0 || !finito(d.p95) || d.p95 <= d.mediana)
        p.push('Debe cumplirse 0 < mediana < percentil 95.');
      if (d.truncarEn !== undefined && !(d.truncarEn > 0))
        p.push('El truncamiento debe ser mayor que cero.');
      break;
  }
  return p;
}
